import React, { useMemo, useState } from 'react';
import apiService from '../../services/apiService';
import { showToast } from '../Toast';
import { DSSheet, DSButton, DSIcon } from '../../design-system/primitives';
import '../../pages/membership/membership.css';

// Ending a membership.
//
// The sheet is built around the one thing that actually needs a decision - when
// it takes effect - and then shows the consequences of that choice as two lists:
// what has to be done now to stop the money, and what comes due on the day.
//
// The "do this now" split is not presentation. Square can only stop a
// subscription from renewing at the END of the current billing period, so a
// payment-stopping step left until the effective date bills the member one more
// time and the club refunds it. That is why it sits above the fold with its own
// heading, and why the heading says why.
//
// Tone is deliberate and was agreed with the admin: this is low drama. Somebody
// is becoming a guest, not being evicted. They keep their credits, their
// history, their join date and their club access.

const MANUAL_STEPS = [
  { key: 'door', label: 'Remove from Master Lock', note: 'Their code no longer opens the door.' },
  { key: 'discord', label: 'Remove the Discord role', note: 'Done by hand in Discord.' },
  { key: 'tab', label: 'Close out the tab', note: 'Settle or write off any negative balance.' }
];

/**
 * What has to happen, split by when.
 *
 * Mirrors offboardingService.paymentSteps on the server. Kept here as well
 * because the sheet has to show the plan BEFORE anything is created, and there
 * is nothing to fetch yet.
 */
export function offboardPlan(method, mode) {
  const square = method === 'SUBSCRIPTION'
    ? [{ key: 'subscription', label: 'Stop the Square subscription from renewing', note: 'Otherwise it charges them again.' }]
    : method === 'INVOICE'
      ? [
        { key: 'invoice', label: 'Cancel the current Square invoice', note: 'Otherwise they are billed for a month they are not a member.' },
        { key: 'invoice_series', label: 'Cancel the recurring invoice series', note: 'Cancelling one invoice does not stop the series. Only you can do this, in the Square Dashboard.' }
      ]
      : [];

  // Ending immediately collapses the distinction: everything is due now.
  return mode === 'now'
    ? { now: square.concat(MANUAL_STEPS), later: [] }
    : { now: square, later: MANUAL_STEPS };
}

function EndMembershipSheet({ userId, name, method, endsOn, onClose, onDone }) {
  const [mode, setMode] = useState('renewal');
  const [checks, setChecks] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const endDate = endsOn ? formatDate(endsOn) : 'the end of the paid month';
  const plan = useMemo(() => offboardPlan(method, mode), [method, mode]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await apiService.endMembership(userId, { mode, effectiveDate: endsOn || undefined });
      showToast(
        mode === 'now'
          ? `${name}'s membership ended. The manual steps stay in the queue.`
          : `Ending scheduled for ${endDate}. ${name} stays a member until then.`,
        mode === 'now' ? 'warning' : 'info'
      );
      if (onDone) onDone();
    } catch (error) {
      showToast(error.response?.data?.error || 'Could not end the membership', 'error');
      setSubmitting(false);
    }
  };

  const toggle = (key) => setChecks((current) => ({ ...current, [key]: !current[key] }));

  return (
    <DSSheet
      title={`End ${name}'s membership`}
      onClose={onClose}
      footer={(
        <div className="offboard__footer">
          <DSButton
            appearance="solid"
            tone={mode === 'now' ? 'danger' : 'primary'}
            className="offboard__confirm"
            onClick={submit}
            disabled={submitting}
          >
            {submitting
              ? 'Working...'
              : (mode === 'now' ? 'End the membership now' : `Schedule the ending for ${endDate}`)}
          </DSButton>
          <DSButton appearance="outline" tone="neutral" onClick={onClose} disabled={submitting}>
            Cancel
          </DSButton>
        </div>
      )}
    >
      <p className="offboard__body">
        Choose when it takes effect. Anything that has to be done by hand is tracked
        here until it is done.
      </p>

      <div className="offboard__modes">
        <ModeCard
          selected={mode === 'renewal'}
          label={`Ends at renewal, ${endDate}`}
          note="They keep full access until then. Use this when they are leaving at the end of a month they have paid for."
          onSelect={() => setMode('renewal')}
        />
        <ModeCard
          selected={mode === 'now'}
          label="End immediately"
          note="Marks them a non-member now. The manual steps stay here as follow-ups."
          onSelect={() => setMode('now')}
        />
      </div>

      <p className="offboard__section-title">
        {mode === 'now' ? 'Do these now' : 'Do this now, so it does not renew'}
      </p>

      {plan.now.length === 0 ? (
        <p className="offboard__note">Nothing to stop in Square for this membership.</p>
      ) : (
        <div className="offboard__steps">
          {plan.now.map((step) => (
            <button
              key={step.key}
              type="button"
              className={`offboard__step ${checks[step.key] ? 'is-on' : ''}`}
              aria-pressed={Boolean(checks[step.key])}
              onClick={() => toggle(step.key)}
            >
              <span className="offboard__box" aria-hidden="true">
                {checks[step.key] ? <DSIcon name="check" size="sm" /> : null}
              </span>
              <span className="offboard__step-main">
                <span className="offboard__step-label">{step.label}</span>
                <span className="offboard__step-note">{step.note}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      {plan.later.length > 0 && (
        <div className="offboard__later">
          <p className="offboard__section-title offboard__section-title--flush">
            On {endDate}, when the membership ends
          </p>
          <ul>
            {plan.later.map((step) => (
              <li key={step.key}>{step.label}</li>
            ))}
          </ul>
          {/* Said plainly so nobody goes looking for them today and concludes
              the software forgot. */}
          <p className="offboard__note">These come back to this queue on the day, not before.</p>
        </div>
      )}
    </DSSheet>
  );
}

function ModeCard({ selected, label, note, onSelect }) {
  return (
    <button
      type="button"
      className={`offboard__mode ${selected ? 'is-on' : ''}`}
      aria-pressed={selected}
      onClick={onSelect}
    >
      <span className="offboard__radio" aria-hidden="true" />
      <span className="offboard__mode-main">
        <span className="offboard__mode-label">{label}</span>
        <span className="offboard__mode-note">{note}</span>
      </span>
    </button>
  );
}

function formatDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export default EndMembershipSheet;
